// Pure radial-menu geometry extracted 1:1 from RoomCanvas.tsx.
// No PIXI, no React, no component state — plain args in, numbers/objects out.
export type RadialPosition = { xOffset: number; yOffset: number };

export const getRadialMenuPositions = (count: number): RadialPosition[] => {
  const positions: RadialPosition[] = [];
  const normalizedCount = Math.max(0, Math.min(count, 6));
  const fullWidthSpacing = 35;
  const horizontalSpacing = 83;
  const topOuterY = -90;
  const topInnerY = topOuterY + fullWidthSpacing; // -55
  const bottomInnerY = -topInnerY; // 55
  const bottomOuterY = -topOuterY; // 90

  if (normalizedCount === 1) {
    positions.push({ xOffset: 0, yOffset: topOuterY });
  } else if (normalizedCount === 2) {
    positions.push({ xOffset: 0, yOffset: topOuterY });
    positions.push({ xOffset: 0, yOffset: bottomOuterY });
  } else if (normalizedCount === 3) {
    positions.push({ xOffset: 0, yOffset: topOuterY });
    positions.push({ xOffset: 0, yOffset: topInnerY });
    positions.push({ xOffset: 0, yOffset: bottomOuterY });
  } else if (normalizedCount === 4) {
    positions.push({ xOffset: 0, yOffset: topOuterY });
    positions.push({ xOffset: 0, yOffset: topInnerY });
    positions.push({ xOffset: 0, yOffset: bottomInnerY });
    positions.push({ xOffset: 0, yOffset: bottomOuterY });
  } else if (normalizedCount === 5) {
    positions.push({ xOffset: 0, yOffset: topOuterY });
    positions.push({ xOffset: -horizontalSpacing, yOffset: topInnerY });
    positions.push({ xOffset: horizontalSpacing, yOffset: topInnerY });
    positions.push({ xOffset: -horizontalSpacing, yOffset: bottomInnerY });
    positions.push({ xOffset: horizontalSpacing, yOffset: bottomInnerY });
  } else if (normalizedCount === 6) {
    positions.push({ xOffset: 0, yOffset: topOuterY });
    positions.push({ xOffset: -horizontalSpacing, yOffset: topInnerY });
    positions.push({ xOffset: horizontalSpacing, yOffset: topInnerY });
    positions.push({ xOffset: -horizontalSpacing, yOffset: bottomInnerY });
    positions.push({ xOffset: horizontalSpacing, yOffset: bottomInnerY });
    positions.push({ xOffset: 0, yOffset: bottomOuterY });
  }

  return positions;
};

export const getClampedMenuCenter = (position: { x: number; y: number }, optionCount: number) => {
  if (typeof window === 'undefined') return position;

  const viewport = window.visualViewport;
  const viewportLeft = viewport ? (viewport.offsetLeft + (viewport.pageLeft || 0)) : 0;
  const viewportTop = viewport ? (viewport.offsetTop + (viewport.pageTop || 0)) : 0;
  const viewportWidth = viewport?.width ?? window.innerWidth;
  const viewportHeight = viewport?.height ?? window.innerHeight;

  const positions = getRadialMenuPositions(optionCount);
  const xOffsets = positions.map((item) => item.xOffset);
  const yOffsets = positions.map((item) => item.yOffset);
  const minXOffset = xOffsets.length ? Math.min(...xOffsets) : 0;
  const maxXOffset = xOffsets.length ? Math.max(...xOffsets) : 0;
  const minYOffset = yOffsets.length ? Math.min(...yOffsets) : 0;
  const maxYOffset = yOffsets.length ? Math.max(...yOffsets) : 0;

  const safePadding = 20;
  const optionHalfWidth = 68; // half of button minWidth (136px) — ensures no button clips off-screen
  const optionHalfHeight = 15;

  const minCenterX = viewportLeft + safePadding + optionHalfWidth - minXOffset;
  const maxCenterX = viewportLeft + viewportWidth - safePadding - optionHalfWidth - maxXOffset;
  const minCenterY = viewportTop + safePadding + optionHalfHeight - minYOffset;
  const maxCenterY = viewportTop + viewportHeight - safePadding - optionHalfHeight - maxYOffset;

  const clampedX = Math.min(Math.max(position.x, minCenterX), maxCenterX);
  const clampedY = Math.min(Math.max(position.y, minCenterY), maxCenterY);

  // console.log('[RADIAL] clamp', { input: position, optionCount, vw: viewportWidth, vh: viewportHeight, minCenterX, maxCenterX, minCenterY, maxCenterY, result: { x: clampedX, y: clampedY } });
  return { x: clampedX, y: clampedY };
};

// Radial menu types and functions
export type RadialMenuActionKey = 'mirror' | 'unplace' | 'buy' | 'buy_all' | 'cancel' | 'end_testdrive' | 'set_footprint' | 'set_surface' | 'wall_height_up' | 'wall_height_down';

export const BASE_RADIAL_MENU_OPTIONS: Array<{ key: Exclude<RadialMenuActionKey, 'buy' | 'cancel'>; icon: string; textKey: string; adminOnly?: boolean }> = [
  {
    key: 'mirror',
    icon: '/ui/house-options/rotate-house.webp',
    textKey: 'Flip',
  },
  {
    key: 'unplace',
    icon: '',
    textKey: 'Hide',
  },
];

export const PREVIEW_BUY_MENU_OPTION: { key: RadialMenuActionKey; icon: string; textKey: string } = {
  key: 'buy',
  icon: '/ui/telegram-stars-icon.png',
  textKey: 'Buy now',
};

export const PREVIEW_BUY_ALL_MENU_OPTION: { key: RadialMenuActionKey; icon: string; textKey: string } = {
  key: 'buy_all',
  icon: '/ui/telegram-stars-icon.png',
  textKey: 'Pay all',
};

export const PREVIEW_CANCEL_MENU_OPTION: { key: RadialMenuActionKey; icon: string; textKey: string } = {
  key: 'cancel',
  icon: '/ui/house-options/abandon-house.webp',
  textKey: 'Cancel',
};

export const PREVIEW_END_TESTDRIVE_OPTION: { key: RadialMenuActionKey; icon: string; textKey: string } = {
  key: 'end_testdrive',
  icon: '/ui/house-options/abandon-house.webp',
  textKey: 'End Preview',
};

export const PREVIEW_MIRROR_MENU_OPTION: { key: RadialMenuActionKey; icon: string; textKey: string } = {
  key: 'mirror',
  icon: '/ui/house-options/rotate-house.webp',
  textKey: 'Flip',
};

export const ADMIN_SET_FOOTPRINT_OPTION: { key: RadialMenuActionKey; icon: string; textKey: string; adminOnly: boolean } = {
  key: 'set_footprint',
  icon: '/ui/house-options/rotate-house.webp',
  textKey: 'Edit footprint',
  adminOnly: true,
};

export const ADMIN_SET_SURFACE_OPTION: { key: RadialMenuActionKey; icon: string; textKey: string; adminOnly: boolean } = {
  key: 'set_surface',
  icon: '/ui/house-options/rotate-house.webp',
  textKey: 'Edit surface',
  adminOnly: true,
};

export const WALL_HEIGHT_UP_OPTION: { key: RadialMenuActionKey; icon: string; textKey: string; adminOnly: boolean } = {
  key: 'wall_height_up',
  icon: '/ui/house-options/rotate-house.webp',
  textKey: 'Height ↑',
  adminOnly: false,
};

export const WALL_HEIGHT_DOWN_OPTION: { key: RadialMenuActionKey; icon: string; textKey: string; adminOnly: boolean } = {
  key: 'wall_height_down',
  icon: '/ui/house-options/rotate-house.webp',
  textKey: 'Height ↓',
  adminOnly: false,
};
